<template>
    <div class="beacon-list">
      <h1>Transceivers List</h1>
      <button class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#createBeaconModal">Create Transceivers</button>
      <table class="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Attached To (as of {{ today }})</th>
            <th>Status</th>
            <th>Inspection Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="beacon in beacons" :key="beacon.beaconid">
            <td v-if="!beacon.isEditing">{{ beacon.beaconnumber }}</td>
            <td v-else>
              <input
                type="text"
                v-model="beacon.beaconnumber"
                @input="validateIntegerInput(beacon)"
                :class="{ 'is-invalid': beacon.errorMessage }"
              />
              <div v-if="beacon.errorMessage" class="invalid-feedback">{{ beacon.errorMessage }}</div>
            </td>
            <td>
              <template v-if="beacon.tripClient && beacon.tripClient.client && beacon.tripClient.client.person">
                <router-link :to="`/clients/${beacon.tripClient.client.clientid}`" class="client-link">
                  {{ beacon.tripClient.client.person.firstname }} {{ beacon.tripClient.client.person.lastname }}
                </router-link>
              </template>
              <template v-else>Unlinked</template>
            </td>
            <td v-if="!beacon.isEditing">{{ beacon.active ? 'Active' : 'Inactive' }}</td>
            <td v-else>
              <select v-model="beacon.active">
                <option :value="true">Active</option>
                <option :value="false">Inactive</option>
              </select>
            </td>
            <td v-if="!beacon.isEditing">{{ beacon.inspectionDate ? formatDateForDisplay(beacon.inspectionDate) : 'N/A' }}</td>
            <td v-else>
              <DatePicker v-model="beacon.inspectionDate" compact clearable />
            </td>
            <td class="action-buttons">
              <button v-if="!beacon.isEditing" class="btn btn-warning" @click="editBeacon(beacon)">Edit</button>
              <button v-if="!beacon.isEditing" class="btn btn-danger" @click="deleteBeacon(beacon.beaconid)">Delete</button>
              <button v-if="beacon.isEditing" class="btn btn-success" @click="saveBeacon(beacon)">Save</button>
              <button v-if="beacon.isEditing" class="btn btn-secondary" @click="cancelEdit(beacon)">Cancel</button>
            </td>
          </tr>
        </tbody>
      </table>
  
      <!-- Create Beacon Modal -->
      <div ref="createBeaconModal" class="modal fade" id="createBeaconModal" tabindex="-1" aria-labelledby="createBeaconModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="createBeaconModalLabel">Create New Transceivers</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="createBeacon">
                <div class="mb-3">
                  <label for="beaconNumber" class="form-label">Transceivers Number</label>
                  <input
                    type="text"
                    class="form-control"
                    id="beaconNumber"
                    v-model="newBeacon.beaconNumber"
                    @input="validateIntegerInput(newBeacon)"
                    :class="{ 'is-invalid': newBeacon.errorMessage }"
                    required
                  />
                  <div v-if="newBeacon.errorMessage" class="invalid-feedback">{{ newBeacon.errorMessage }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Inspection Date</label>
                  <DatePicker v-model="newBeacon.inspectionDate" clearable />
                </div>
                <button type="submit" class="btn btn-primary">Create</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import EquipmentDataService from "@/services/EquipmentDataService";
  import DatePicker from "../Shared/DatePicker.vue";
  import { todayLocalDateString } from "@/utils/dateHelpers";

  export default {
    components: { DatePicker },
    data() {
      return {
        beacons: [],
        newBeacon: {
          beaconNumber: '',
          inspectionDate: '',
          errorMessage: null
        },
        today: todayLocalDateString()
      };
    },
    methods: {
      fetchBeacons() {
        EquipmentDataService.getAllBeacons()
          .then(response => {
            this.beacons = response.data.map(beacon => ({
              ...beacon,
              isEditing: false,
              errorMessage: null,
              originalData: { ...beacon }
            }))
            // Sort beacons numerically by beacon number
            .sort((a, b) => {
              const numA = parseInt(a.beaconnumber) || 0;
              const numB = parseInt(b.beaconnumber) || 0;
              return numA - numB;
            });
          })
          .catch(error => {
            console.error("Error fetching beacons:", error);
          });
      },
      createBeacon() {
        if (this.newBeacon.errorMessage) return; // Prevent submission if there's an error

        const beaconData = {
          beaconNumber: this.newBeacon.beaconNumber,
          inspectionDate: this.formatDateForSave(this.newBeacon.inspectionDate)
        };

        EquipmentDataService.createBeacon(beaconData)
          .then(() => {
            this.fetchBeacons();
            this.closeModalAndReset();
          })
          .catch(error => {
            if (error.response && error.response.data.message === "Duplicate beacon") {
              this.newBeacon.errorMessage = "This Transiever number already exists.";
            } else {
              console.error("Error creating beacon:", error);
            }
          });
      },
      editBeacon(beacon) {
        beacon.isEditing = true;
        beacon.errorMessage = null;
        // Ensure the date is in the correct format for the date input
        if (beacon.inspectionDate) {
          beacon.inspectionDate = this.formatDateForInput(beacon.inspectionDate);
        }
      },
      saveBeacon(beacon) {
        if (beacon.errorMessage) return; // Prevent save if there's an error
        EquipmentDataService.updateBeacon(beacon.beaconid, {
          beaconnumber: beacon.beaconnumber,
          active: beacon.active,
          inspectiondate: this.formatDateForSave(beacon.inspectionDate)
        })
          .then(() => {
            beacon.isEditing = false;
            this.fetchBeacons();
          })
          .catch(error => {
            if (error.response && error.response.data.message === "Duplicate beacon") {
              beacon.errorMessage = "This Transiever number already exists.";
            } else {
              console.error("Error updating beacon:", error);
            }
          });
      },
      cancelEdit(beacon) {
        Object.assign(beacon, beacon.originalData);
        beacon.isEditing = false;
        beacon.errorMessage = null;
      },
      deleteBeacon(beaconId) {
        EquipmentDataService.deleteBeacon(beaconId)
          .then(() => {
            this.fetchBeacons();
          })
          .catch(error => {
            console.error("Error deleting beacon:", error);
          });
      },
      validateIntegerInput(beacon) {
        const isInteger = /^\d+$/.test(beacon.beaconNumber || beacon.beaconnumber);
        beacon.errorMessage = isInteger ? null : "Transiever number must be an integer.";
      },
      closeModalAndReset() {
        const modal = this.$refs.createBeaconModal;
        modal.style.display = 'none';
        document.body.classList.remove('modal-open');
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.parentNode.removeChild(backdrop);
        }
        this.newBeacon.beaconNumber = '';
        this.newBeacon.inspectionDate = '';
        this.newBeacon.errorMessage = null;
      },
      formatDateForDisplay(dateValue) {
        if (!dateValue) return 'N/A';

        // If it's already a string in YYYY-MM-DD format, convert to display format
        if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateValue.split('-');
          return `${month}/${day}/${year}`;
        }

        // If it's a Date object or ISO string, extract just the date part
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 'N/A';

        // Use UTC methods to avoid timezone conversion
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');

        return `${month}/${day}/${year}`;
      },
      formatDateForSave(dateValue) {
        if (!dateValue) return null;

        // If it's already in YYYY-MM-DD format, return as is
        if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue;
        }

        // If it's a Date object, extract just the date part
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return null;

        // Use UTC methods to avoid timezone conversion
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      },
      formatDateForInput(dateValue) {
        if (!dateValue) return '';

        // If it's already in YYYY-MM-DD format, return as is
        if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue;
        }

        // If it's a Date object or ISO string, extract just the date part
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';

        // Use UTC methods to avoid timezone conversion
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      }
    },
    created() {
      this.fetchBeacons();
    }
  };
  </script>
  
  <style scoped>
  .action-buttons {
    display: flex;
    gap: 5px;
  }
  .action-buttons button {
    padding: 0.25rem 0.5rem;
    border-radius: 5px;
    display: flex;
    align-items: center;
  }
  .is-invalid {
    border-color: #dc3545;
  }
  .invalid-feedback {
    color: #dc3545;
    font-size: 0.875em;
    display: block;
  }
  </style>
  